// ============================================================
// src/pages/Login.tsx - Updated Jan 2, 2026
// ============================================================
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  TextField,
  Typography,
  Link as MuiLink,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
} from "@mui/material";
import { Visibility, VisibilityOff, Apple } from "@mui/icons-material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { GoogleLogin, CredentialResponse } from "@react-oauth/google";
import AppleSignin from "react-apple-signin-auth";
import { useAuth } from "../../hooks/useAuth";

const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || "";

interface FormData {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, socialLogin } = useAuth();
  const redirectPath = searchParams.get("redirect");
  const postLoginPath = redirectPath ? `/${redirectPath.replace(/^\//, '')}` : "/dashboard";
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleGoogleSuccess = async (response: CredentialResponse) => {
    if (!response.credential) {
      setError("No credential received from Google");
      return;
    }

    setSocialLoading(true);
    setError(null);

    try {
      const { isNewUser } = await socialLogin({
        provider: "google",
        token: response.credential,
      });

      // Navigate based on whether user is new (needs onboarding) or existing
      if (isNewUser) {
        navigate("/onboarding/plan");
      } else {
        navigate(postLoginPath);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Google sign-in failed. Please try again.");
      setSocialLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError("Google sign-in was cancelled or failed.");
  };

  const handleAppleSuccess = async (response: any) => {
    const idToken = response.authorization?.id_token;
    if (!idToken) {
      setError("No credential received from Apple");
      return;
    }

    setSocialLoading(true);
    setError(null);

    try {
      const { isNewUser } = await socialLogin({
        provider: "apple",
        token: idToken,
        // Apple only provides name on first sign-in, pass it if available
        firstName: response.user?.name?.firstName,
        lastName: response.user?.name?.lastName,
      });

      if (isNewUser) {
        navigate("/onboarding/plan");
      } else {
        navigate(postLoginPath);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Apple sign-in failed. Please try again.");
      setSocialLoading(false);
    }
  };

  const handleAppleError = (error: any) => {
    // User cancelled - don't show error
    if (error?.error === "popup_closed_by_user") {
      return;
    }
    setError("Apple sign-in was cancelled or failed.");
  };

  const isAnyLoading = loading || socialLoading;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError(null);

    try {
      await login(data.email, data.password);
      navigate(postLoginPath);
    } catch (err: any) {
      setError(err.response?.data?.message || "Login failed. Please check your credentials and try again.");
      setLoading(false);
    }
  };

  return (
    <Box maxWidth={400} mx="auto" mt={8}>
      <Typography variant="h4" gutterBottom>
        Log in
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Social Sign-In Buttons */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, mb: 2 }}>
          {socialLoading ? (
            <CircularProgress size={40} />
          ) : (
            <>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                size="large"
                width="350"
                text="continue_with"
              />
              {APPLE_CLIENT_ID && (
                <AppleSignin
                  authOptions={{
                    clientId: APPLE_CLIENT_ID,
                    scope: "email name",
                    redirectURI: window.location.origin,
                    usePopup: true,
                  }}
                  uiType="dark"
                  onSuccess={handleAppleSuccess}
                  onError={handleAppleError}
                  render={(props: any) => (
                    <Button
                      {...props}
                      variant="outlined"
                      startIcon={<Apple />}
                      sx={{
                        width: 350,
                        height: 40,
                        textTransform: "none",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#000",
                        borderColor: "#000",
                        backgroundColor: "#fff",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                          borderColor: "#000",
                        },
                      }}
                    >
                      Continue with Apple
                    </Button>
                  )}
                />
              )}
            </>
          )}
        </Box>

        <Divider sx={{ my: 2 }}>or</Divider>

        <TextField
          label="Email"
          fullWidth
          margin="normal"
          autoComplete="email"
          disabled={isAnyLoading}
          {...register("email", { required: "Email is required" })}
          error={!!errors.email}
          helperText={errors.email?.message}
        />
        <TextField
          label="Password"
          type={showPassword ? "text" : "password"}
          fullWidth
          margin="normal"
          autoComplete="current-password"
          disabled={isAnyLoading}
          {...register("password", { required: "Password is required" })}
          error={!!errors.password}
          helperText={errors.password?.message}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 2 }}
          disabled={isAnyLoading}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Log in with Email"}
        </Button>
      </form>
      <Typography variant="body2" align="center" sx={{ mt: 2 }}>
        <MuiLink component={Link} to="/forgot-password">Forgot password?</MuiLink>
      </Typography>
      <Typography variant="body2" align="center" sx={{ mt: 1 }}>
        Need an account? <MuiLink component={Link} to="/register">Sign up</MuiLink>
      </Typography>
    </Box>
  );
};

export default Login;